



































































import { Prop, Vue, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { Action, Modules } from './store/store';
import { SubBrand, Theme } from './store/modules/themes';

import Battery from './components/battery/battery.vue';
import Faq from './components/faq/faq.vue';
import Headline from './components/headline/headline.vue';
import SubHeadline from './components/sub-headline/sub-headline.vue';
import VehicleSelection from './components/vehicle-selection/vehicle-selection.vue';
import ModelSelection from './components/model-selection/model-selection.vue';
import ChargingOptions from './components/charging-options/charging-options.vue';
import Mileage from './components/mileage/mileage.vue';
import { BoundingBox, ComponentConfig, Locale } from './store/modules/config';
import { ApiInformation, ChargingOption, Vehicle } from './store/modules/charging-data.interface';
import { isNafta } from './config/translation/available-locales';

const themeModule = namespace(Modules.themes);
const configModule = namespace(Modules.config);
const chargingDataModule = namespace(Modules.chargingData);

@Component({
  name: 'eqweb-ctc',
  components: { Mileage, ChargingOptions, Battery, Faq, Headline, SubHeadline, VehicleSelection, ModelSelection },
})
export default class Ctc extends Vue {
  private appBoundingBoxObserver: ResizeObserver | undefined = undefined;

  @Prop()
  public readonly theme!: string;

  @Prop()
  public readonly subBrand!: string;

  @Prop()
  public readonly asSidebar!: boolean;

  @Prop()
  public readonly preSelectedModelSeries!: string;

  @Prop()
  public readonly preSelectedVehicleIds!: string[];

  @Prop()
  public readonly stage!: string;

  @Prop()
  public readonly locale!: string;

  @Prop()
  public readonly chargingOptions!: ChargingOption[];

  @Prop()
  public readonly componentConfig!: ComponentConfig;

  @Prop()
  public readonly apiInformation!: ApiInformation;

  @Prop({ default: null })
  public readonly preSelectedVehicle!: Vehicle | null;

  @configModule.Action('setStage')
  private readonly setStage!: Action<string>;

  @configModule.Action('setLocale')
  private readonly setLocale!: Action<Locale>;

  @configModule.Action('setAsSidebar')
  private readonly setAsSidebar!: Action<boolean>;

  @chargingDataModule.Action('setChargingOptions')
  private readonly setChargingOptions!: Action<ChargingOption[]>;

  @chargingDataModule.Action('loadModels')
  private readonly loadModels!: Action<string, Promise<void>>;

  @chargingDataModule.Action('setPreSelectedVehicleIds')
  private readonly setPreSelectedVehicleIds!: Action<string[]>;

  @chargingDataModule.Action('setModelSeries')
  private readonly setModelSeries!: Action<string>;

  @chargingDataModule.Action('setModelsByPredefinedVehicle')
  private readonly setModelsByPredefinedVehicle!: Action<Vehicle>;

  @chargingDataModule.Getter('hasModels')
  private readonly hasModels!: boolean;

  @configModule.Action('setComponentConfig')
  private readonly setComponentConfig!: Action<ComponentConfig>;

  @configModule.Getter('showDebugToggleButton')
  private readonly showDebugToggleButton!: boolean;

  @themeModule.Getter('propsTheme')
  private readonly propsTheme!: Theme;

  @chargingDataModule.Getter('loading')
  private readonly loading!: boolean;

  @chargingDataModule.Getter('selectedVehicle')
  private readonly selectedVehicle!: Vehicle;

  @chargingDataModule.Action('setSelectedVehicle')
  private readonly setSelectedVehicle!: Action<Vehicle>;

  @themeModule.State('subBrand')
  private readonly propsSubBrand!: SubBrand;

  @themeModule.Action('setTheme')
  private readonly setTheme!: Action<string>;

  @themeModule.Action('setSubBrand')
  private readonly setSubBrand!: Action<string>;

  @chargingDataModule.Action('setApiInformation')
  private readonly setApiInformation!: Action<ApiInformation>;

  @configModule.Action('setAppBoundingBox')
  private readonly setAppBoundingBox!: Action<BoundingBox>;

  @configModule.Getter('hasBatteryMileage')
  private readonly hasBatteryMileage!: boolean;

  @configModule.Getter('asSidebar')
  private readonly propsAsSidebar!: boolean;

  public created(): void {
    this.setModelSeries(this.preSelectedModelSeries);
    this.setPreSelectedVehicleIds(this.preSelectedVehicleIds);
    this.setStage(this.stage);
    const [language, country] = this.locale.split('_');
    this.setLocale({
      language,
      country,
    });
    this.setTheme(this.theme);
    this.setSubBrand(isNafta(this.locale) ? SubBrand.usa : this.subBrand ? this.subBrand : SubBrand.eq);
    this.setAsSidebar(this.asSidebar);
    this.setChargingOptions(this.chargingOptions);
    this.setComponentConfig(this.componentConfig);
    this.setApiInformation(this.apiInformation);

    // should be last calls
    // only fetch bodyTypes for vehicle selection if not defined in context.
    if (!this.preSelectedVehicle) {
      this.loadModels(this.locale);
    } else {
      this.setModelsByPredefinedVehicle(this.preSelectedVehicle);
    }
  }

  public toggleTheme(): void {
    this.setTheme(this.propsTheme === Theme.light ? Theme.dark : Theme.light);
  }

  public toggleBrand(): void {
    this.setSubBrand(this.propsSubBrand === SubBrand.usa ? SubBrand.eq : SubBrand.usa);
  }

  public toggleAsSidebar(): void {
    this.setAsSidebar(this.propsAsSidebar ? false : true);
  }

  public mounted(): void {
    this.startAppBoundingBoxObserver();
  }

  public updated(): void {
    this.startAppBoundingBoxObserver();
  }

  public destroyed(): void {
    if (this.appBoundingBoxObserver) {
      this.appBoundingBoxObserver.unobserve(this.$el);
    }
  }

  private startAppBoundingBoxObserver(): void {
    // TODO: solve better than this. $nextTick didn't work unfortunately
    setTimeout(() => {
      if (this.$el?.clientWidth) {
        this.setAppBoundingBox({ width: this.$el?.clientWidth, height: this.$el?.clientHeight });
      }
    }, 20);

    if (window && this.$el?.clientWidth && this.$el?.clientHeight) {
      if (this.appBoundingBoxObserver) {
        this.appBoundingBoxObserver.unobserve(this.$el);
      }
      this.appBoundingBoxObserver = new window.ResizeObserver(() => {
        this.setAppBoundingBox({ width: this.$el?.clientWidth, height: this.$el?.clientHeight });
      });
      this.appBoundingBoxObserver.observe(this.$el);
    }
  }

  private get disclaimerText(): string {
    return this.$t('DISCLAIMER', {
      name: this.selectedVehicle.name,
      acPower: this.selectedVehicle.installedAcChargingPower,
      dcPower: this.selectedVehicle.installedDcChargingPower,
    }) as string;
  }

  @Watch('preSelectedModelSeries')
  public preSelectedModelSeriesChanged(newPreSelectedModelSeries: string): void {
    this.setModelSeries(newPreSelectedModelSeries);
  }
  @Watch('preSelectedVehicle')
  public preSelectedVehicleChanged(newVehicleData: Vehicle | undefined): void {
    if (newVehicleData) {
      this.setSelectedVehicle(newVehicleData);
    }
  }
  @Watch('apiInformation')
  public apiInformationChanged(newApiInformation: ApiInformation): void {
    this.setApiInformation(newApiInformation);
  }

  @Watch('chargingOptions')
  public chargingOptionsChanged(newChargingOptions: ChargingOption[]): void {
    this.setChargingOptions(newChargingOptions);
  }

  @Watch('componentConfig')
  public componentConfigChange(newConfig: ComponentConfig): void {
    this.setComponentConfig(newConfig);
  }

  @Watch('stage')
  public stageChanged(newStage: string): void {
    this.setStage(newStage);
  }

  @Watch('asSidebar')
  public asSidebarChanged(newAsSidebar: boolean): void {
    this.setAsSidebar(newAsSidebar);
  }

  @Watch('locale')
  public localeChanged(newLocale: string): void {
    const localeSplit = this.locale.split('_');

    this.setLocale({
      language: localeSplit[0],
      country: localeSplit[1],
    });
    this.loadModels(newLocale);
  }

  @Watch('preSelectedVehicleIds')
  public preSelectedVehicleModelIdsChanged(newModelIds: string[]): void {
    this.setPreSelectedVehicleIds(newModelIds);
  }

  @Watch('subBrand')
  public subBrandChanged(newSubBrand: string): void {
    this.setSubBrand(newSubBrand);
  }

  @Watch('theme')
  public themeChanged(newTheme: string): void {
    this.setTheme(newTheme);
  }

  @Watch('hasModels')
  public hasModelsChanged(): void {
    this.startAppBoundingBoxObserver();
  }
}
